/**
 * Shared helpers for the AI DOS admin pages so labels, severities, equipment
 * lists, and clipboard behavior cannot drift between pages.
 */
import type { ToastServiceMethods } from "primevue/toastservice";

type ToastLike = Pick<ToastServiceMethods, "add">;

export const AI_DOS_LANES = ["performance", "runlife", "cost"] as const;
export type AiDosLane = (typeof AI_DOS_LANES)[number];

/**
 * Mirrors REQUIRED_CANDIDATE_EQUIPMENT_TYPES in
 * falcon/ai_dos_candidate_generation.py. Update both together.
 */
export const REQUIRED_CANDIDATE_EQUIPMENT_TYPES = [
  "pump",
  "pump_housing",
  "motor",
  "intake",
  "seal",
  "sensor",
  "mle",
  "downhole_cable",
  "surface_cable",
  "vsd",
  "switchboard",
  "step_up_transformer",
  "step_down_transformer"
] as const;

/**
 * Mirrors EQUIPMENT_CONFIG keys in falcon/generic_material_search.py.
 * Update both together.
 */
export const MATERIAL_SEARCH_EQUIPMENT_TYPES = [
  "pump",
  "pump_housing",
  "motor",
  "intake",
  "gas_separator",
  "seal",
  "sensor",
  "mle",
  "downhole_cable",
  "surface_cable",
  "vsd",
  "switchboard",
  "step_up_transformer",
  "step_down_transformer"
] as const;

export const equipmentTypeLabel = (equipmentType: string) =>
  equipmentType
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const laneLabel = (lane: string) => {
  if (lane === "runlife") return "Run-life";
  return lane.charAt(0).toUpperCase() + lane.slice(1);
};

export const statusSeverity = (status: string) => {
  if (status === "pass") return "success";
  if (status === "warning") return "warning";
  return "danger";
};

export const confidenceSeverity = (confidence: string) => {
  if (confidence === "high") return "success";
  if (confidence === "medium") return "warning";
  return "danger";
};

export const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

export const copyTextToClipboard = async (toast: ToastLike, value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.add({ severity: "success", summary: "Copied", life: 2500 });
  } catch {
    toast.add({
      severity: "error",
      summary: "Copy failed",
      detail: "Clipboard access was denied by the browser.",
      life: 4000
    });
  }
};

const parseJsonString = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed || !["{", "["].includes(trimmed[0])) return value;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
};

export const normalizeJsonForDisplay = (value: unknown): unknown => {
  if (typeof value === "string") {
    const parsed = parseJsonString(value);
    return parsed === value ? value : normalizeJsonForDisplay(parsed);
  }
  if (Array.isArray(value)) {
    return value.map(item => normalizeJsonForDisplay(item));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        normalizeJsonForDisplay(item)
      ])
    );
  }
  return value;
};

export const copyJsonToClipboard = (toast: ToastLike, value: unknown) =>
  copyTextToClipboard(
    toast,
    JSON.stringify(normalizeJsonForDisplay(value), null, 2)
  );
